import { PropsWithChildren, useContext, useMemo } from 'react';

import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';

import {
  AccessControlContext,
  AccessControlProvider as SharedAccessControlProvider,
  type ControlDataBySubjectId,
} from '@cast/shared-access-control-data-access';
import { useClustersQuery } from '@cast/shared-cluster-context-data-access';

import { useUser } from '@cast/infrastructure-auth';

type Props = {
  useCurrentUser?: boolean;
};

export const AccessControlProvider = ({
  useCurrentUser,
  children,
}: PropsWithChildren<Props>) => {
  const user = useUser();
  return (
    <SharedAccessControlProvider useCurrentUser={useCurrentUser} user={user}>
      {children}
    </SharedAccessControlProvider>
  );
};

type UseAccessControlDataProps = {
  excludeOrphanedClusters?: boolean;
};

type UseAccessControlDataResult<ClusterIdsSet extends Set<string> | null> = {
  isLoading: boolean;
  isLoadingAccessControlData: boolean;
  accessControlData: ControlDataBySubjectId;
  knownClusterIds: ClusterIdsSet;
  refetch: () => Promise<void>;
  error: boolean;
};

export function useAccessControlData(args: {
  excludeOrphanedClusters: true;
}): UseAccessControlDataResult<Set<string>>;
export function useAccessControlData(args: {
  excludeOrphanedClusters: false;
}): UseAccessControlDataResult<null>;
export function useAccessControlData({
  excludeOrphanedClusters,
}: UseAccessControlDataProps): UseAccessControlDataResult<Set<string> | null>;
export function useAccessControlData({
  excludeOrphanedClusters = true,
}: UseAccessControlDataProps) {
  const ctx = useContext(AccessControlContext);
  const { clusters, isLoading: isLoadingClusters } = useClustersQuery({
    enabled: excludeOrphanedClusters && !ctx.isLoading,
  });

  const {
    filteredData,
    knownClusterIds,
    isLoading: isLoadingAccessControlData,
  } = useMemo(() => {
    if (!excludeOrphanedClusters) {
      return {
        filteredData: ctx.accessControlData,
        knownClusterIds: null,
        isLoading: ctx.isLoading,
      };
    }

    const clustersById = keyBy(clusters, 'id');
    return {
      filteredData: mapValues(ctx.accessControlData, (accessData) => ({
        ...accessData,
        clusters: pickBy(
          accessData.clusters,
          (cluster) => cluster.id in clustersById
        ),
      })),
      knownClusterIds: new Set(Object.keys(clustersById)),
      isLoading: ctx.isLoading || isLoadingClusters,
    };
  }, [
    excludeOrphanedClusters,
    clusters,
    ctx.accessControlData,
    ctx.isLoading,
    isLoadingClusters,
  ]);

  return {
    isLoading: ctx.isLoading,
    isLoadingAccessControlData,
    accessControlData: filteredData,
    knownClusterIds,
    refetch: ctx.refetch,
    error: ctx.error,
  };
}

type UseAccessControlProps = {
  subjectId?: string;
  excludeOrphanedClusters?: boolean;
};
export const useAccessControl = ({
  subjectId,
  excludeOrphanedClusters = true,
}: UseAccessControlProps) => {
  const accessCtx = useAccessControlData({
    excludeOrphanedClusters,
  });

  if (!subjectId) {
    return {
      isLoading: true,
      subjectAccessData: {
        organizations: {},
        clusters: {},
      },
      refetch: () => Promise.resolve(),
      error: false,
    };
  }

  return {
    isLoading: accessCtx.isLoading,
    subjectAccessData: accessCtx.accessControlData[subjectId],
    refetch: accessCtx.refetch,
    error: accessCtx.error,
  };
};
